import { COREG_META_KEY } from './coregRegistration';

const STORAGE_PREFIX = 'isicRcmCoreg:';

/** In-memory fallback when localStorage is blocked or full. */
const memoryCache = new Map();

export function getCoregFolderKey(folder) {
  if (!folder) return null;
  return folder._id || folder.id || folder.name || null;
}

function storageKey(folderId) {
  return `${STORAGE_PREFIX}${folderId}`;
}

function readFromStorage(storage, folderId) {
  const raw = storage.getItem(storageKey(folderId));
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (!parsed?.[COREG_META_KEY]) return null;
  return parsed;
}

function writeToStorage(storage, folderId, payload) {
  storage.setItem(storageKey(folderId), JSON.stringify(payload));
}

/** @returns {{ savedAt: string, [COREG_META_KEY]: object, registrationData?: array } | null} */
export function loadCoregFromLocalStorage(folderId) {
  if (!folderId) return null;

  const cached = memoryCache.get(folderId);
  if (cached) return cached;

  try {
    return readFromStorage(localStorage, folderId)
      ?? readFromStorage(sessionStorage, folderId);
  } catch (err) {
    console.warn('Failed to read co-registration from browser storage:', err);
    return null;
  }
}

export function saveCoregToLocalStorage(folderId, metaPatch) {
  if (!folderId || !metaPatch?.[COREG_META_KEY]) return false;

  const payload = {
    savedAt: new Date().toISOString(),
    [COREG_META_KEY]: metaPatch[COREG_META_KEY],
    registrationData: metaPatch.registrationData ?? null,
  };

  memoryCache.set(folderId, payload);

  try {
    writeToStorage(localStorage, folderId, payload);
    return true;
  } catch (err) {
    console.warn('localStorage save failed, trying sessionStorage:', err);
  }

  try {
    writeToStorage(sessionStorage, folderId, payload);
    return true;
  } catch (err) {
    console.warn('sessionStorage save failed; using in-memory cache only:', err);
  }

  // Still usable for this page session via memoryCache + onRegistrationUpdated.
  return true;
}

/** Merge browser-saved coreg into folder meta (local wins over server when present). */
export function mergeLocalCoregIntoFolder(folder) {
  const folderId = getCoregFolderKey(folder);
  if (!folderId) return folder;

  const local = loadCoregFromLocalStorage(folderId);
  if (!local) return folder;

  return mergeCoregMetaIntoFolder(folder, local);
}

/** Apply saved coreg payload onto a folder object. */
export function mergeCoregMetaIntoFolder(folder, metaPatch) {
  if (!folder || !metaPatch?.[COREG_META_KEY]) return folder;

  return {
    ...folder,
    meta: {
      ...folder.meta,
      [COREG_META_KEY]: metaPatch[COREG_META_KEY],
      registrationData: metaPatch.registrationData ?? folder.meta?.registrationData,
    },
  };
}
